import { View, StyleSheet, ActivityIndicator } from "react-native";
import { useState, useCallback } from "react";
import { useFocusEffect } from "@react-navigation/native";
import AdminLayout from "../../layouts/AdminLayout.web";
import { MotiView } from "moti";
import CustomText from "../../components/common/general/CustomText/CustomText";
import CustomSpacing from "../../components/common/layout/CustomSpacing/CustomSpacing";
import CustomButton from "../../components/common/general/CustomButton/CustomButton";
import CourseTabNavigator from "../../components/adminDashboard/CourseTabNavigator/CourseTabNavigator";
import AdminCourseInformationContainer from "../../containers/adminDashboard/AdminCourseInformationContainer/AdminCourseInformationContainer";
import AdminRosterManagementContainer from "../../containers/adminDashboard/AdminRosterManagementContainer/AdminRosterManagementContainer";
import AdminCommunityContainer from "../../containers/adminDashboard/AdminCommunityContainer/AdminCommunityContainer";
import AdminDueDateControlsContainer from "../../containers/adminDashboard/AdminDueDateControlsContainer/AdminDueDateControlsContainer";
import AdminCourseCatalogueInfoContainer from "../../containers/adminDashboard/AdminCourseCatalogueInfoContainer/AdminCourseCatalogueInfoContainer";
import AdminAnnouncementsContainer from "../../containers/adminDashboard/AdminAnnouncementsContainer/AdminAnnouncementsContainer";
import { NavigationProp, useNavigation, useRoute, RouteProp } from "@react-navigation/native";
import type { RootStackParamList } from "../../navigation/navigation.types";
import { useGetCourseAdminStatsQuery } from "../../graphql/generated/graphql";
import { EyeIcon, PencilIcon, ChartLineIcon, ClipboardTextIcon, UsersIcon, CopySimpleIcon, CheckIcon, ArrowLeftIcon } from "@phosphor-icons/react";
import Tooltip from "../../components/common/tooltip/Tooltip";
import { useAppConfig } from "../../AppConfigProvider";
import { usePreviewMode } from "../../contexts/PreviewModeContext";
import { CourseInformationTab } from "../../constants/courseInformationTabs";

export { CourseInformationTab };

const StudentsIcon = () => <UsersIcon color="#FFFFFF" size={18} />;
const AssignmentsIcon = () => <ClipboardTextIcon color="#FFFFFF" size={18} />;
const GradeIcon = () => <ChartLineIcon color="#FFFFFF" size={18} />;
const PreviewIcon = () => <EyeIcon color="#3E68FE" size={24} />;
const EditIcon = () => <PencilIcon color="#FFFFFF" size={24} />;
const CopyIcon = ({ isCopied }: { isCopied: boolean }) =>
  isCopied ? <CheckIcon color="#10B981" size={24} /> : <CopySimpleIcon color="#3E68FE" size={24} />;

const TAB_DATA = [
  { key: CourseInformationTab.COURSE_INFORMATION, title: "Course Information" },
  { key: CourseInformationTab.ROSTER_MANAGEMENT, title: "Roster Management" },
  { key: CourseInformationTab.COMMUNITY, title: "Community" },
  { key: CourseInformationTab.DUE_DATE_CONTROLS, title: "Due Date Controls" },
  { key: CourseInformationTab.ANNOUNCEMENTS, title: "Announcements" },
  { key: CourseInformationTab.COURSE_CATALOG_INFO, title: "Course Catalog Info" },
];

function CourseInformationView() {
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();
  const route = useRoute<RouteProp<RootStackParamList, "Course Information">>();
  const { enterPreviewMode } = usePreviewMode();
  const appConfig = useAppConfig();

  const courseId = route.params?.courseId;
  const [activeTab, setActiveTab] = useState(CourseInformationTab.COURSE_INFORMATION);
  const [isLinkCopied, setIsLinkCopied] = useState(false);

  useFocusEffect(
    useCallback(() => {
      if (route.params?.activeTab) {
        const tabParam = route.params.activeTab as CourseInformationTab;
        if (Object.values(CourseInformationTab).includes(tabParam)) {
          setActiveTab(tabParam);
        }
      }
    }, [route.params?.activeTab])
  );

  const {
    data: courseData,
    loading,
    error,
  } = useGetCourseAdminStatsQuery({
    variables: { id: courseId },
    skip: !courseId,
  });

  const course = courseData?.getCourseAdminStats?.data?.[0];

  if (loading) {
    return (
      <AdminLayout withPadding={false}>
        <View style={styles.pageBackground}>
          <View style={styles.loaderContainer}>
            <ActivityIndicator size="large" color="#0000ff" />
          </View>
        </View>
      </AdminLayout>
    );
  }

  if (error || !course) {
    return (
      <AdminLayout withPadding={false}>
        <View style={styles.pageBackground}>
          <View style={styles.errorContainer}>
            <CustomText text="Course not found" size="l" useTranslationText={false} />
          </View>
        </View>
      </AdminLayout>
    );
  }

  // Calculate stats
  const enrolledStudents = course.enrolledStudents?.length || 0;
  const assignmentCount = course.modules?.reduce((total, module) => total + (module.assignments?.length || 0), 0) || 0;
  // Placeholder for average grade - we need to implement this calculation
  // hide for now
  // const averageGrade = 87; // Placeholder

  const handlePreviewCourse = () => {
    // Enter preview mode before navigating
    enterPreviewMode(course.id, course.name);
    navigation.navigate("Custom Course Student", {
      course_id: course.id,
    });
  };

  const handleCopyCourseLink = () => {
    const courseLink = `https://${appConfig.currentDomain}/student/school/course?course_id=${course.id}`;
    navigator.clipboard.writeText(courseLink);
    setIsLinkCopied(true);
    setTimeout(() => setIsLinkCopied(false), 1000);
  };

  const handleCourseEditor = () => {
    const courseName = course?.name || "Unknown Course";
    const returnTo = route.params?.returnTo || "Manage Courses";

    navigation.navigate("Custom Coursework Modules", {
      courseId: course.id,
      courseName,
      returnTo,
      parentReturnTo: returnTo,
    });
  };

  const handleBackToTeaching = () => {
    const returnTo = route.params?.returnTo || "Teaching";
    navigation.navigate(returnTo as never);
  };

  // to be used with course code and term/semester. being removed for now 1/9/2026
  // const formatCourseNameWithCode = (courseName: string, courseCode?: string | null): string => {
  //   if (courseCode && courseCode.trim()) {
  //     return `${courseCode} • ${courseName}`;
  //   }
  //   return courseName;
  // };

  const getCourseStatus = () => {
    if (course.isDraft) {
      return { text: "DRAFT", backgroundColor: "#FEF3C7", textColor: "#92400E" };
    }
    return { text: "PUBLISHED", backgroundColor: "#D1FAE5", textColor: "#065F46" };
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case CourseInformationTab.COURSE_INFORMATION:
        return <AdminCourseInformationContainer courseId={courseId} />;
      case CourseInformationTab.ROSTER_MANAGEMENT:
        return <AdminRosterManagementContainer courseId={courseId} />;
      case CourseInformationTab.COMMUNITY:
        return <AdminCommunityContainer courseId={courseId} />;
      case CourseInformationTab.DUE_DATE_CONTROLS:
        return <AdminDueDateControlsContainer courseId={courseId} />;
      case CourseInformationTab.ANNOUNCEMENTS:
        return <AdminAnnouncementsContainer courseId={courseId} />;
      case CourseInformationTab.COURSE_CATALOG_INFO:
        return <AdminCourseCatalogueInfoContainer courseId={courseId} />;
      default:
        return null;
    }
  };

  return (
    <AdminLayout withPadding={false}>
      <View style={styles.pageBackground}>
        <View style={styles.contentContainer}>
          <MotiView
            from={{
              opacity: 0,
              translateY: 20,
              scale: 1,
            }}
            animate={{
              opacity: 1,
              translateY: 0,
              scale: 1,
            }}
            delay={10}
            // @ts-expect-error - transition is not typed
            transition={{ type: "timing", duration: 500 }}>
            {/* Back Button */}
            <CustomButton
              text="Back"
              styleType="transparent"
              onPress={handleBackToTeaching}
              useTranslationText={false}
              style={styles.backButton}
              textStyle={styles.backButtonText}
              leftIcon={<ArrowLeftIcon size={16} color="#64748B" />}
            />

            <CustomSpacing type="vertical" size="l" />

            {/* Header and Stats Container */}
            <View style={styles.headerStatsContainer}>
              {/* Header Section */}
              <View style={styles.headerContainer}>
                <View style={styles.headerContent}>
                  <View style={styles.titleSection}>
                    <CustomText
                      text={course.name}
                      useTranslationText={false}
                      textType="display"
                      weight="ultraBold"
                      role="heading"
                      aria-level="1"
                      style={styles.courseTitle}
                    />
                    <View style={[styles.publishedBadge, { backgroundColor: getCourseStatus().backgroundColor }]}>
                      <CustomText
                        text={getCourseStatus().text}
                        size="xs"
                        weight="medium"
                        useTranslationText={false}
                        style={[styles.publishedText, { color: getCourseStatus().textColor }]}
                      />
                    </View>
                  </View>

                  <CustomText
                    text={course.course_code || ""}
                    size="m"
                    weight="medium"
                    useTranslationText={false}
                    style={styles.courseSubtitle}
                  />
                </View>

                <View style={styles.headerButtons}>
                  <Tooltip content={isLinkCopied ? "Link copied!" : "Copy course link"} horizontalOffset={-56}>
                    <CustomButton
                      text=""
                      styleType="primaryLight"
                      size="m"
                      bold={true}
                      leftIcon={<CopyIcon isCopied={isLinkCopied} />}
                      onPress={handleCopyCourseLink}
                      useTranslationText={false}
                      style={styles.previewButton}
                    />
                  </Tooltip>
                  <Tooltip content="Preview course" horizontalOffset={-52}>
                    <CustomButton
                      text=""
                      styleType="primaryLight"
                      size="m"
                      bold={true}
                      leftIcon={<PreviewIcon />}
                      onPress={handlePreviewCourse}
                      useTranslationText={false}
                      style={styles.previewButton}
                    />
                  </Tooltip>
                  <CustomButton
                    text="Course Editor"
                    styleType="primary"
                    size="m"
                    bold={true}
                    leftIcon={<EditIcon />}
                    onPress={handleCourseEditor}
                    useTranslationText={false}
                  />
                </View>
              </View>

              <CustomSpacing type="vertical" size="xl" />

              {/* Stats Cards */}
              <View style={styles.statsContainer}>
                <View style={styles.statCard}>
                  <View style={[styles.statIcon, styles.studentsIcon]}>
                    <StudentsIcon />
                  </View>
                  <View style={styles.statContent}>
                    <CustomText
                      text={enrolledStudents.toString()}
                      size="xxl"
                      weight="ultraBold"
                      useTranslationText={false}
                      style={styles.statNumber}
                    />
                    <CustomText
                      text="ENROLLED STUDENTS"
                      size="xs"
                      weight="medium"
                      useTranslationText={false}
                      style={styles.statLabel}
                    />
                  </View>
                </View>

                <View style={styles.statCard}>
                  <View style={[styles.statIcon, styles.assignmentsIcon]}>
                    <AssignmentsIcon />
                  </View>
                  <View style={styles.statContent}>
                    <CustomText
                      text={assignmentCount.toString()}
                      size="xxl"
                      weight="ultraBold"
                      useTranslationText={false}
                      style={styles.statNumber}
                    />
                    <CustomText
                      text="ASSIGNMENTS"
                      size="xs"
                      weight="medium"
                      useTranslationText={false}
                      style={styles.statLabel}
                    />
                  </View>
                </View>

                {/* hidding until we can properly calculate */}
                {/* <View style={styles.statCard}>
                  <View style={[styles.statIcon, styles.gradeIcon]}>
                    <GradeIcon />
                  </View>
                  <View style={styles.statContent}>
                    <CustomText
                      text={`${averageGrade}%`}
                      size="xxl"
                      weight="ultraBold"
                      useTranslationText={false}
                      style={styles.statNumber}
                    />
                    <CustomText
                      text="AVERAGE GRADE"
                      size="xs"
                      weight="medium"
                      useTranslationText={false}
                      style={styles.statLabel}
                    />
                  </View>
                </View> */}
              </View>
            </View>

            <CustomSpacing type="vertical" size="xl" />

            {/* Tab Navigator */}
            <View style={styles.tabContainer}>
              <CourseTabNavigator
                tabData={TAB_DATA}
                activeTab={activeTab}
                onTabPress={(tabKey: string) => setActiveTab(tabKey as CourseInformationTab)}
              />

              {renderTabContent()}
            </View>
          </MotiView>
        </View>
      </View>
    </AdminLayout>
  );
}

const styles = StyleSheet.create({
  pageBackground: {
    flex: 1,
    backgroundColor: "#F2F4F7",
    // @ts-expect-error - web only
    minHeight: "100vh",
  },
  contentContainer: {
    padding: 24,
    flex: 1,
  },
  loaderContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  errorContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  backButton: {
    alignSelf: "flex-start",
    paddingHorizontal: 0,
  },
  backButtonText: {
    color: "#64748B",
    fontSize: 16,
  },
  headerStatsContainer: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: 24,
    marginHorizontal: 0,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowRadius: 3,
    shadowOpacity: 0.1,
  },
  headerContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  headerContent: {
    flex: 1,
  },
  titleSection: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  courseTitle: {
    color: "#1e293b",
    fontSize: 28,
  },
  publishedBadge: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 20,
  },
  publishedText: {
    fontSize: 12,
    letterSpacing: 0.5,
  },
  courseSubtitle: {
    color: "#64748b",
    marginTop: 4,
  },
  headerButtons: {
    flexDirection: "row",
    gap: 12,
  },
  previewButton: {
    backgroundColor: "#fff",
    borderWidth: 1,
    borderColor: "#D1D5DB",
  },
  statsContainer: {
    flexDirection: "row",
    gap: 24,
  },
  statCard: {
    flex: 1,
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    padding: 18,
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
  },
  statIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    justifyContent: "center",
    alignItems: "center",
  },
  studentsIcon: {
    backgroundColor: "#913DAF",
  },
  assignmentsIcon: {
    backgroundColor: "#1A8E9D",
  },
  // gradeIcon: {
  //   backgroundColor: "#22C55E",
  // },
  statContent: {
    flex: 1,
  },
  statNumber: {
    color: "#101828",
    lineHeight: 32,
  },
  statLabel: {
    color: "#6B7280",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginTop: 4,
  },
  tabContainer: {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    overflow: "hidden",
  },
});

export default CourseInformationView;
